import { useState, useEffect, useRef, useCallback } from "react";

interface Line {
  text: string;
  width: number;
}

interface PretextResult {
  lines: Line[];
  height: number;
  ready: boolean;
  containerRef: React.RefObject<HTMLElement | null>;
}

/**
 * Uses @chenglou/pretext to measure text layout without DOM thrashing.
 * Awaits font loading, resolves computed fontSize for clamp() values,
 * and re-measures on resize.
 */
export function usePretextLayout(
  text: string,
  fontSpec: string,
  lineHeight: number
): PretextResult {
  const containerRef = useRef<HTMLElement | null>(null);
  const [result, setResult] = useState<{ lines: Line[]; height: number }>({ lines: [], height: 0 });
  const [ready, setReady] = useState(false);

  const measure = useCallback(async () => {
    if (typeof window === "undefined" || !containerRef.current) return;

    // Wait for web fonts to load so measurements are accurate
    await document.fonts.ready;

    const { prepareWithSegments, layoutWithLines } = await import("@chenglou/pretext");

    const el = containerRef.current;
    const computedStyle = getComputedStyle(el);
    const resolvedSize = parseFloat(computedStyle.fontSize);

    // Replace any clamp/calc fontSize in the fontSpec with the resolved pixel value
    const resolvedFontSpec = fontSpec.replace(
      /clamp\([^)]+\)|calc\([^)]+\)|[\d.]+px/,
      `${resolvedSize}px`
    );

    const maxWidth = el.clientWidth || el.offsetWidth || 600;
    const prepared = prepareWithSegments(text, resolvedFontSpec);
    const layoutResult = layoutWithLines(prepared, maxWidth, lineHeight * resolvedSize);

    setResult({
      lines: layoutResult.lines.map((line: { text: string; width: number }) => ({
        text: line.text,
        width: line.width,
      })),
      height: layoutResult.height,
    });
    setReady(true);
  }, [text, fontSpec, lineHeight]);

  useEffect(() => {
    measure();

    const el = containerRef.current;
    if (!el) return;

    const ro = new ResizeObserver(() => measure());
    ro.observe(el);
    return () => ro.disconnect();
  }, [measure]);

  return { ...result, ready, containerRef };
}
